import { Link, useParams, useSearch } from "@tanstack/react-router";
import {
  Briefcase,
  Building,
  Building2,
  FileText,
  GitCompareArrows,
  Layers,
  Pencil,
} from "lucide-react";
import { useState } from "react";
import { RoleEmployeesSection } from "@/components/roles/RoleEmployeesSection";
import { RoleFormDialog } from "@/components/roles/RoleFormDialog";
import { RoleLevelExpectations } from "@/components/roles/RoleLevelExpectations";
import { RoleLevelBadges } from "@/components/roles/RoleLevelsField";
import { RoleRequirementsEditor } from "@/components/roles/RoleRequirementsEditor";
import { ActivityLog } from "@/components/shared/activity-log";
import { DetailSection, MetadataRow } from "@/components/shared/detail-section";
import { EmptyState } from "@/components/shared/empty-state";
import { type DetailMetaItem, EntityDetailHeader } from "@/components/shared/entity-detail-header";
import { EntityDetailPage } from "@/components/shared/entity-detail-page";
import { LifecycleBadge } from "@/components/shared/lifecycle-badge";
import { TabBar, type TabItem } from "@/components/shared/tab-bar";
import { Button } from "@/components/ui/button";
import { useRoleLevelOptions } from "@/hooks/use-role-level-applicability";
import { useRole } from "@/hooks/use-roles";
import { useSession } from "@/lib/auth-client";
import { parseOriginSearch } from "@/lib/detail-origin";
import { canEditAll } from "@/lib/permissions";

const TAB_KEYS = ["overview", "requirements", "certifications", "employees", "history"] as const;
type TabKey = (typeof TAB_KEYS)[number];

const TABS: TabItem[] = [
  { key: "overview", label: "Overview" },
  { key: "requirements", label: "Required capabilities" },
  { key: "certifications", label: "Required certifications" },
  { key: "employees", label: "Employees" },
  { key: "history", label: "Change history" },
];

function isTabKey(value: unknown): value is TabKey {
  return TAB_KEYS.some((k) => k === value);
}

export function RoleDetailPage() {
  const params = useParams({ strict: false });
  const roleId = typeof params.roleId === "string" ? params.roleId : "";
  const query = useRole(roleId);
  // Read-only here — configuring the set is done through the main Edit dialog.
  const { data: roleLevelOptions = [] } = useRoleLevelOptions(roleId);
  const { data: session } = useSession();
  const isAdmin = canEditAll(session?.user?.role);
  const [editing, setEditing] = useState(false);

  // `tab` lets a back-link from a person page reopen a specific tab (e.g.
  // Employees). The rest of the origin handling lives in EntityDetailPage; this
  // is the same pure read of the same search params.
  const parsed = parseOriginSearch(useSearch({ strict: false }));
  const [tab, setTab] = useState<TabKey>(() => (isTabKey(parsed.tab) ? parsed.tab : "overview"));

  // Career levels sit under the role name. Read-only here — the picker lives in
  // the main Edit dialog. Omitted entirely (not an empty row) when the role has
  // no ladder, so the header doesn't grow a blank gap.
  const hasLevels = roleLevelOptions.some((o) => o.applies);
  const levelBadges = hasLevels ? <RoleLevelBadges options={roleLevelOptions} /> : undefined;

  return (
    <EntityDetailPage
      query={query}
      entityLabel="Role"
      section="Roles"
      list={{ label: "Roles", to: "/dashboard/roles" }}
      name={query.data?.name}
      // The expectation grid needs the full window — with all five rating
      // columns showing, 72rem forces the prose into a sideways scroll.
      wide
    >
      {(role) => {
        const meta: DetailMetaItem[] = [];
        if (role.pillarName) meta.push({ key: "pillar", icon: Building2, node: role.pillarName });
        if (role.departmentName)
          meta.push({ key: "dept", icon: Building, node: role.departmentName });
        if (role.roleTeamName) meta.push({ key: "team", icon: Layers, node: role.roleTeamName });
        if (role.jobDescriptionUrl) {
          meta.push({
            key: "jd",
            icon: FileText,
            node: (
              <a
                href={role.jobDescriptionUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="text-primary hover:underline"
              >
                Job description
              </a>
            ),
          });
        }

        // Whether the Overview tab has anything at all to show.
        const hasOverviewDetail = Boolean(
          role.description || role.pillarName || role.departmentName || role.roleTeamName,
        );

        return (
          <>
            <EntityDetailHeader
              visual={
                <span className="flex size-20 shrink-0 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
                  <Briefcase className="size-8" />
                </span>
              }
              title={role.name}
              titleBadges={levelBadges}
              subtitle={role.description}
              meta={meta}
              badges={<LifecycleBadge status={role.status} />}
              actions={
                <>
                  <Button asChild variant="outline" size="sm">
                    <Link to="/dashboard/roles/compare" search={{ targetRole: role.id }}>
                      <GitCompareArrows className="mr-1.5 size-3.5" />
                      Compare
                    </Link>
                  </Button>
                  {isAdmin && (
                    <Button size="sm" onClick={() => setEditing(true)}>
                      <Pencil className="mr-1.5 size-3.5" />
                      Edit
                    </Button>
                  )}
                </>
              }
            />

            <TabBar tabs={TABS} value={tab} onChange={(k) => setTab(k as TabKey)} />

            {tab === "overview" && (
              <div className="space-y-4">
                {/*
                A role with nothing recorded used to render four dashes under two
                headings, which reads as the page having failed rather than as the
                record being empty — and on the sandbox catalogue that is most of
                them. Say so once, and tell an administrator what to do about it.
              */}
                {hasOverviewDetail ? (
                  <>
                    <DetailSection title="Overview">
                      <MetadataRow label="Description" value={role.description} />
                    </DetailSection>
                    <DetailSection title="Classification">
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                        <MetadataRow label="Pillar" value={role.pillarName} />
                        <MetadataRow label="Department" value={role.departmentName} />
                        <MetadataRow label="Role Team" value={role.roleTeamName} />
                      </div>
                    </DetailSection>
                  </>
                ) : (
                  <DetailSection title="Overview">
                    <EmptyState
                      icon={FileText}
                      title="Nothing recorded for this role yet"
                      description={
                        isAdmin
                          ? "Add a description and place it in a pillar, department and role team so people can find it and know what it is for."
                          : "No description, pillar, department or role team has been set. An administrator can add them."
                      }
                      action={
                        isAdmin ? (
                          <Button size="sm" onClick={() => setEditing(true)}>
                            <Pencil className="mr-1.5 size-3.5" />
                            Add details
                          </Button>
                        ) : undefined
                      }
                    />
                  </DetailSection>
                )}
              </div>
            )}

            {tab === "requirements" && (
              <DetailSection title="Required skills & competencies">
                <div className="space-y-6">
                  {/* The expectation grid names every required skill and competency in
                    its first column, so listing them again underneath was redundant
                    — the flat lists render only when there's no grid to replace
                    them (a role with no career levels). */}
                  <RoleLevelExpectations
                    roleId={role.id}
                    roleName={role.name}
                    levels={roleLevelOptions}
                    isAdmin={isAdmin}
                  />
                  {/* The grid is read-only about WHICH capabilities the role
                    requires, so the edit affordance stays either way; the flat
                    lists render only when there's no grid naming them. */}
                  <RoleRequirementsEditor
                    roleId={role.id}
                    roleName={role.name}
                    isAdmin={isAdmin}
                    sections={["skills", "competencies"]}
                    showLists={!hasLevels}
                  />
                </div>
              </DetailSection>
            )}

            {tab === "certifications" && (
              <DetailSection title="Required certifications">
                <RoleRequirementsEditor
                  roleId={role.id}
                  roleName={role.name}
                  isAdmin={isAdmin}
                  sections={["certifications"]}
                  showLists
                />
              </DetailSection>
            )}

            {tab === "employees" && (
              <DetailSection title="Employees">
                <RoleEmployeesSection roleId={role.id} roleName={role.name} />
              </DetailSection>
            )}

            {tab === "history" && (
              <DetailSection title="Change history">
                <ActivityLog
                  entityType="role"
                  entityId={role.id}
                  fieldLabels={{
                    pillarId: "Pillar",
                    departmentId: "Department",
                    roleTeamId: "Role Team",
                    jobDescriptionUrl: "Job description URL",
                  }}
                />
              </DetailSection>
            )}

            {isAdmin && (
              <RoleFormDialog
                key={role.updatedAt}
                open={editing}
                onOpenChange={setEditing}
                role={role}
              />
            )}
          </>
        );
      }}
    </EntityDetailPage>
  );
}
